import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.history.back();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div
          className="min-h-screen flex items-center justify-center p-6 bg-white"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-red-50 rounded-2xl mx-auto mb-5">
              <i className="ri-error-warning-line text-3xl text-red-400" />
            </div>

            <h1 className="text-2xl font-700 text-gray-900 mb-2">Algo deu errado</h1>
            <p className="text-gray-500 text-sm mb-2">
              Ocorreu um erro inesperado. Isso pode ser um problema temporário.
            </p>

            {this.state.error && (
              <div className="mb-6 p-3 bg-gray-50 border border-gray-100 rounded-xl text-left">
                <p className="text-xs text-gray-400 font-mono break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-arrow-left-line text-sm" />
                Voltar
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-refresh-line text-sm" />
                Tentar novamente
              </button>
            </div>

            <button
              onClick={this.handleGoHome}
              className="mt-4 text-sm text-gray-400 hover:text-emerald-600 underline cursor-pointer"
            >
              Ir para a página inicial
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
